import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Website",
  "Google Visibility",
  "Social Media Setup",
  "Lead Generation",
  "Automation",
  "Not Sure Yet",
];

const INITIAL = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  website: "",
  service_need: "",
  message: "",
  consent: false,
};

const inputClass =
  "w-full rounded-xl border border-sky-500/15 bg-navy-950/70 px-4 py-3 text-sm text-white placeholder:text-slate-600 transition-colors duration-300 focus:border-sky-400 focus:outline-none";

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your name.";
    if (form.business_name.trim().length < 2) er.business_name = "Please enter your business name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Please enter a valid email address.";
    if (form.phone.trim().length < 7) er.phone = "Please enter your phone number.";
    if (!form.service_need) er.service_need = "Please choose an option.";
    if (form.message.trim().length < 10) er.message = "Tell us a little more (at least 10 characters).";
    if (!form.consent) er.consent = "Please tick the consent box so we can reply to you.";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Enquiry sent — thank you. We'll be in touch shortly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const Err = ({ k }) =>
    errors[k] ? <p data-testid={`contact-error-${k}`} className="mt-1.5 text-xs text-red-400">{errors[k]}</p> : null;

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28">
      <div className="section-shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="micro-label">Contact</p>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
              Get your free business review
            </h2>
            <p className="mt-5 text-sm sm:text-base leading-relaxed text-slate-400 max-w-md">
              Tell us about your business and what you'd like to improve. We'll review your current
              online presence and come back with clear, practical recommendations — no obligation.
            </p>
          </Reveal>
          <div className="mt-9 space-y-5">
            {[
              { n: "01", t: "Send the form", d: "Two minutes is all it takes." },
              { n: "02", t: "We review your presence", d: "Website, Google visibility and enquiry journey." },
              { n: "03", t: "You get clear next steps", d: "Plain-English recommendations for your business." },
            ].map((s) => (
              <Reveal key={s.n} delay={0.06}>
                <div className="flex gap-4 items-start">
                  <span className="font-mono text-xs font-semibold text-sky-400 border border-sky-500/25 rounded-full w-9 h-9 flex items-center justify-center shrink-0">{s.n}</span>
                  <div>
                    <p className="font-heading font-bold text-white text-sm">{s.t}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="glass-panel rounded-3xl p-6 sm:p-10">
            {done ? (
              <div data-testid="contact-form-success" className="text-center py-14">
                <CheckCircle2 size={52} className="mx-auto text-emerald-400" strokeWidth={1.5} />
                <h3 className="mt-6 font-heading text-2xl font-extrabold text-white">Thank you — enquiry received</h3>
                <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-md mx-auto leading-relaxed">
                  We've got your details and a confirmation is on its way to your inbox. We'll be in
                  touch shortly with your free business review.
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another"
                  onClick={() => setDone(false)}
                  className="btn-ghost mt-8"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="cf-name" className="block text-xs font-bold text-slate-300 mb-2">Name *</label>
                    <input id="cf-name" data-testid="contact-input-name" type="text" autoComplete="name" className={inputClass} placeholder="e.g. Alex Morgan" value={form.name} onChange={set("name")} />
                    <Err k="name" />
                  </div>
                  <div>
                    <label htmlFor="cf-business" className="block text-xs font-bold text-slate-300 mb-2">Business Name *</label>
                    <input id="cf-business" data-testid="contact-input-business" type="text" autoComplete="organization" className={inputClass} placeholder="e.g. Morgan Electrical Services" value={form.business_name} onChange={set("business_name")} />
                    <Err k="business_name" />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="block text-xs font-bold text-slate-300 mb-2">Email *</label>
                    <input id="cf-email" data-testid="contact-input-email" type="email" autoComplete="email" className={inputClass} placeholder="you@yourbusiness.co.uk" value={form.email} onChange={set("email")} />
                    <Err k="email" />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="block text-xs font-bold text-slate-300 mb-2">Phone *</label>
                    <input id="cf-phone" data-testid="contact-input-phone" type="tel" autoComplete="tel" className={inputClass} placeholder="e.g. 07700 900123" value={form.phone} onChange={set("phone")} />
                    <Err k="phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-website" className="block text-xs font-bold text-slate-300 mb-2">Website <span className="text-slate-500 font-normal">(optional)</span></label>
                    <input id="cf-website" data-testid="contact-input-website" type="text" inputMode="url" className={inputClass} placeholder="www.yourbusiness.co.uk" value={form.website} onChange={set("website")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-need" className="block text-xs font-bold text-slate-300 mb-2">What does your business need help with? *</label>
                    <select id="cf-need" data-testid="contact-select-service" className={`${inputClass} appearance-none ${form.service_need ? "" : "text-slate-600"}`} value={form.service_need} onChange={set("service_need")}>
                      <option value="" disabled>Select an option…</option>
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o} className="text-white bg-navy-900">{o}</option>
                      ))}
                    </select>
                    <Err k="service_need" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-message" className="block text-xs font-bold text-slate-300 mb-2">Message *</label>
                    <textarea id="cf-message" data-testid="contact-input-message" rows={4} className={`${inputClass} resize-y`} placeholder="Tell us about your business and what you'd like to achieve…" value={form.message} onChange={set("message")} />
                    <Err k="message" />
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="cf-consent" className="flex items-start gap-3 cursor-pointer select-none">
                    <input id="cf-consent" data-testid="contact-checkbox-consent" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 w-4.5 h-4.5 w-[18px] h-[18px] rounded border-sky-500/30 bg-navy-950 accent-sky-500" />
                    <span className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      I agree to SourceWorld Automations contacting me about my enquiry. Your details are
                      only used to respond to you — see our{" "}
                      <Link to="/privacy-policy" className="text-sky-400 underline underline-offset-2 hover:text-sky-300 transition-colors">
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </label>
                  <Err k="consent" />
                </div>
                <button type="submit" data-testid="contact-form-submit-button" disabled={sending} className="btn-primary mt-8 w-full sm:w-auto disabled:opacity-60 disabled:pointer-events-none">
                  {sending ? (
                    <>Sending <Loader2 size={16} className="animate-spin" /></>
                  ) : (
                    <>Request My Free Review <ArrowRight size={16} strokeWidth={2.5} /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
